import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Settings as Cog, ShieldAlert, KeyRound, Mail } from "lucide-react";

export default function GrowthSettings() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keyDraft, setKeyDraft] = useState({ JSEARCH_API_KEY: "", SERPER_API_KEY: "", YOUTUBE_API_KEY: "" });
  const [kwDraft, setKwDraft] = useState("");
  const [serperKwDraft, setSerperKwDraft] = useState("");
  const [recipientsDraft, setRecipientsDraft] = useState("");
  const [briefSending, setBriefSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/settings");
      setData(data);
      setKwDraft((data.settings.keywords_jsearch || []).join("\n"));
      setSerperKwDraft((data.settings.keywords_serper || []).join("\n"));
      setRecipientsDraft((data.settings.daily_brief_recipients || []).join(", "));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data: resp } = await api.put("/growth/settings", patch);
      setData((d) => ({ ...d, settings: resp.settings }));
      toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const toggleMaster = (v) => save({ master_ai_paused: v });
  const toggleLane = (key, v) => save({ lanes: { [key]: v } });
  const saveKeywords = () => save({ keywords_jsearch: kwDraft.split("\n").map((k) => k.trim()).filter(Boolean) });
  const saveSerperKeywords = () => save({ keywords_serper: serperKwDraft.split("\n").map((k) => k.trim()).filter(Boolean) });
  const saveRecipients = () => {
    const list = recipientsDraft.split(/[,\n]/).map((e) => e.trim()).filter(Boolean);
    save({ daily_brief_recipients: list });
  };
  const toggleBrief = (v) => save({ daily_brief_enabled: v });
  const saveBriefHour = (v) => save({ daily_brief_hour_utc: parseInt(v, 10) });

  const sendBriefNow = async () => {
    setBriefSending(true);
    try {
      const { data } = await api.post("/growth/daily-brief/send-now");
      if (data.sent > 0) toast.success(`Sent to ${data.sent} recipient(s).`);
      else toast.warning(`Not sent: ${data.skipped || "no recipients"}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBriefSending(false); }
  };
  const saveKeys = () => {
    const toSend = Object.fromEntries(Object.entries(keyDraft).filter(([, v]) => v && v.trim()));
    if (!Object.keys(toSend).length) return toast.error("Enter at least one key to save.");
    save({ api_keys: toSend }).then(() => setKeyDraft({ JSEARCH_API_KEY: "", SERPER_API_KEY: "", YOUTUBE_API_KEY: "" }));
  };
  const saveThreshold = (v) => save({ confidence_threshold: v });

  if (loading || !data) return <div className="p-10 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading settings…</div>;

  const s = data.settings;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto" data-testid="growth-settings">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><Cog className="h-7 w-7 text-indigo-400" /> Growth Settings</h1>
        <p className="text-slate-400 text-sm mt-1">Keyword lists, lane toggles, API keys, scoring thresholds, kill switch. Red-tier changes are super-admin only.</p>
      </div>

      {/* Master kill switch */}
      <Card className={`${s.master_ai_paused ? "bg-rose-500/10 border-rose-500/40" : "bg-slate-900/60 border-slate-800"}`}>
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-white font-extrabold flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-300" /> Pause all AI automation</p>
            <p className="text-sm text-slate-400 mt-1">Freezes every discovery lane and every draft action. Does not touch the live customer product.</p>
          </div>
          <Switch
            checked={!!s.master_ai_paused}
            disabled={!isSuper || saving}
            onCheckedChange={toggleMaster}
            data-testid="master-pause-toggle"
          />
        </CardContent>
      </Card>
      {!isSuper && <p className="text-xs text-slate-500">Signed in as Growth Manager — kill switch, API keys, and permission tiers are super-admin only.</p>}

      {/* Lane toggles */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-white font-extrabold text-lg">Discovery lanes</h2>
          <p className="text-xs text-slate-500">Toggle each lane independently. Paid lanes require an API key set below.</p>
          <div className="space-y-1">
            {data.lanes_catalog.map((lane) => (
              <div key={lane.key} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0" data-testid={`lane-row-${lane.key}`}>
                <div>
                  <p className="text-sm font-bold text-white">{lane.name}</p>
                  <p className="text-xs text-slate-500">
                    <span className={lane.tier === "paid" ? "text-amber-300" : "text-emerald-300"}>{lane.tier}</span>
                    {lane.needs_key && <> · needs <code className="text-indigo-300">{lane.needs_key}</code></>}
                  </p>
                </div>
                <Switch
                  checked={!!s.lanes?.[lane.key]}
                  disabled={saving}
                  onCheckedChange={(v) => toggleLane(lane.key, v)}
                  data-testid={`lane-toggle-${lane.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API keys */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-white font-extrabold text-lg flex items-center gap-2"><KeyRound className="h-5 w-5 text-indigo-300" /> API keys</h2>
          {!isSuper ? (
            <p className="text-xs text-slate-500">Super admin only.</p>
          ) : (
            <>
              <div className="space-y-2 text-xs text-slate-400">
                {["JSEARCH_API_KEY", "SERPER_API_KEY", "YOUTUBE_API_KEY"].map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-48 font-mono text-slate-300">{k}</span>
                    <span className="text-slate-500">current: <code>{(s.api_keys_masked || {})[k] || "unset"}</code></span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2">
                {Object.keys(keyDraft).map((k) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{k}</Label>
                    <Input type="password" value={keyDraft[k]} onChange={(e) => setKeyDraft({ ...keyDraft, [k]: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="paste to replace" data-testid={`key-input-${k}`} />
                  </div>
                ))}
              </div>
              <Button onClick={saveKeys} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500 mt-2" data-testid="save-keys-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save keys
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* JSearch keywords */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-white font-extrabold text-lg">JSearch keywords</h2>
          <p className="text-xs text-slate-500">One per line. Public job postings containing these phrases become scored opportunities.</p>
          <Textarea rows={8} value={kwDraft} onChange={(e) => setKwDraft(e.target.value)} className="bg-slate-800 border-slate-700 font-mono text-sm" data-testid="kw-textarea" />
          <Button onClick={saveKeywords} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-kw-btn">Save keywords</Button>
        </CardContent>
      </Card>

      {/* Serper keywords */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-white font-extrabold text-lg">Serper SERP keywords</h2>
          <p className="text-xs text-slate-500">One per line. Deep Google-SERP queries catch long-tail commission-pay discussions (forum threads, Quora, blog comments) that Alerts and JSearch miss.</p>
          <Textarea rows={8} value={serperKwDraft} onChange={(e) => setSerperKwDraft(e.target.value)} className="bg-slate-800 border-slate-700 font-mono text-sm" data-testid="serper-kw-textarea" />
          <Button onClick={saveSerperKeywords} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-serper-kw-btn">Save keywords</Button>
        </CardContent>
      </Card>

      {/* Daily Brief */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-white font-extrabold text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-300" /> Daily Brief email</h2>
            <Switch checked={!!s.daily_brief_enabled} disabled={saving} onCheckedChange={toggleBrief} data-testid="brief-toggle" />
          </div>
          <p className="text-xs text-slate-500">7 AM email with the three recommended actions, lane counts, trials-at-risk, new-users-yesterday, and the goal line.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Send hour (UTC, 0-23)</Label>
              <Input type="number" min={0} max={23} value={s.daily_brief_hour_utc ?? 12} onChange={(e) => saveBriefHour(e.target.value)} className="bg-slate-800 border-slate-700" data-testid="brief-hour-input" />
              <p className="text-[10px] text-slate-500 mt-1">12 UTC = 7 AM Central · 14 UTC = 7 AM Pacific</p>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Recipients</Label>
              <Input value={recipientsDraft} onChange={(e) => setRecipientsDraft(e.target.value)} placeholder="comma-separated — defaults to every super admin" className="bg-slate-800 border-slate-700" data-testid="brief-recipients-input" />
              <Button size="sm" variant="outline" onClick={saveRecipients} disabled={saving} className="border-slate-700 bg-slate-800 text-xs mt-2" data-testid="save-recipients-btn">Save recipients</Button>
            </div>
          </div>

          <div>
            <Button onClick={sendBriefNow} disabled={briefSending || !isSuper} className="bg-emerald-600 hover:bg-emerald-500" data-testid="send-brief-now-btn">
              {briefSending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send brief now
            </Button>
            {!isSuper && <p className="text-[10px] text-slate-500 mt-1">Super admin only.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Confidence threshold */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-white font-extrabold text-lg">AI confidence threshold</h2>
          <p className="text-xs text-slate-500">Below this, opportunities route to <b>Needs human review</b> instead of the main radar.</p>
          <div className="flex items-center gap-4">
            <input
              type="range" min={0} max={1} step={0.05}
              value={s.confidence_threshold || 0.55}
              onChange={(e) => saveThreshold(parseFloat(e.target.value))}
              className="flex-1"
              data-testid="threshold-slider"
            />
            <span className="font-extrabold text-white w-14 text-right">{Math.round((s.confidence_threshold || 0) * 100)}%</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
